import React, { useState, useCallback, useMemo } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../utils/userErrors';
import { Plus, Search, Users, X } from 'lucide-react';
import AppLoader from '../components/modules/AppLoader';
import ModuleAlert from '../components/modules/ModuleAlert';
import ModulePageHeader from '../components/modules/ModulePageHeader';
import { getThemeColor } from '../config/appConfig';
import { useToast } from '../contexts/ToastContext';
import apiService from '../utils/apiService';
import { useFetchOnceByKey } from '../hooks/useMountOnce';
import UpdateCommissionModal from './refer/UpdateCommissionModal';
import '../styles/module-shared.css';
import './refer/refer-a-friend.css';
import './subPartners.css';

const THEME = getThemeColor('primary');

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPercent = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  return `${n % 1 === 0 ? n.toFixed(0) : n}%`;
};

const buildCommissionBySubPartnerId = (referPayload) => {
  const data = referPayload?.result ?? referPayload?.data ?? referPayload ?? {};
  const referrals = pick(data, 'referrals', 'Referrals') || [];
  const map = new Map();
  (Array.isArray(referrals) ? referrals : []).forEach((row) => {
    const subPartnerId = Number(pick(row, 'affiliateSubPartnerId', 'AffiliateSubPartnerId') || 0);
    if (subPartnerId <= 0) return;
    const percentageRaw = pick(row, 'percentage', 'Percentage');
    const percentage =
      percentageRaw == null || percentageRaw === '' ? null : Number(percentageRaw);
    map.set(subPartnerId, {
      inviteId: Number(pick(row, 'affiliateReferSubPartnerId', 'AffiliateReferSubPartnerId') || 0),
      percentage: Number.isFinite(percentage) ? percentage : null,
    });
  });
  return map;
};

const mapSubPartner = (item, index, commissionBySubId = new Map()) => {
  const isActiveRaw = pick(item, 'isActive', 'IsActive');
  const isActive =
    isActiveRaw === true ||
    isActiveRaw === 1 ||
    String(isActiveRaw).toLowerCase() === 'true';

  const subPartnerId = Number(pick(item, 'affiliateUserId', 'AffiliateUserId', 'id') ?? 0);
  const commission = commissionBySubId.get(subPartnerId) || {};
  const email = String(pick(item, 'email', 'Email') || '—');
  const name = String(pick(item, 'fullName', 'FullName') || '').trim();

  return {
    id: subPartnerId > 0 ? subPartnerId : index,
    inviteId: Number(commission.inviteId) || 0,
    subPartnerId,
    name: name || email,
    email,
    affiliateCode: String(pick(item, 'affiliateCode', 'AffiliateCode') || '—'),
    createdDateTime:
      pick(item, 'createdDateTime', 'CreatedDateTime', 'createdDate', 'CreatedDate') || null,
    isActive,
    percentage: commission.percentage ?? null,
    totalClients: Number(pick(item, 'totalClients', 'TotalClients') ?? 0),
    totalLots: Number(pick(item, 'totalLots', 'TotalLots') ?? 0),
    totalDeposit: Number(pick(item, 'totalDeposit', 'TotalDeposit') ?? 0),
    totalWithdrawal: Number(pick(item, 'totalWithdrawal', 'TotalWithdrawal') ?? 0),
    netDeposit: Number(pick(item, 'netDeposit', 'NetDeposit') ?? 0),
  };
};

const formatMoney = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(
    Number.isFinite(value) ? value : 0
  );

const formatLots = (value) =>
  (Number.isFinite(value) ? value : 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Local calendar day (YYYY-MM-DD) of a date value, for inclusive From/To comparisons. */
const toLocalDay = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const Affiliates = () => {
  const { showSuccess, showError } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAffiliateEmail, setNewAffiliateEmail] = useState('');
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [partners, setPartners] = useState([]);
  const [commissionTarget, setCommissionTarget] = useState(null);
  const [updatingCommission, setUpdatingCommission] = useState(false);

  const fetchSubPartners = useCallback(async ({ cancelled }) => {
    try {
      setListLoading(true);
      setListError('');
      const [response, referRes] = await Promise.all([
        apiService.getSubPartnersList(),
        apiService.getReferSubPartners().catch(() => ({ success: false, data: null })),
      ]);
      if (cancelled()) return;
      if (!response.success) {
        setPartners([]);
        setListError(response.error || 'Failed to load sub partners');
        return;
      }
      const raw = response?.data?.result ?? response?.data ?? response?.result ?? [];
      const list = Array.isArray(raw) ? raw : [];
      const commissionBySubId = referRes?.success
        ? buildCommissionBySubPartnerId(referRes.data)
        : new Map();
      setPartners(list.map((item, index) => mapSubPartner(item, index, commissionBySubId)));
    } catch (err) {
      if (!cancelled()) {
        setPartners([]);
        setListError(toUserMessage(err, ERROR_MESSAGES.load));
      }
    } finally {
      if (!cancelled()) setListLoading(false);
    }
  }, []);

  useFetchOnceByKey('sub-partners-list-v4', fetchSubPartners, true);

  const handleUpdateCommission = async (row, commissionPercentage) => {
    setUpdatingCommission(true);
    try {
      const res = await apiService.updateReferSubPartnerCommission({
        affiliateReferSubPartnerId: row.inviteId,
        affiliateSubPartnerId: row.subPartnerId,
        percentage: commissionPercentage,
      });
      if (!res.success) {
        showError(res.error || res.message || 'Failed to update commission');
        return false;
      }
      showSuccess(res.message || 'Commission updated');
      await fetchSubPartners({ cancelled: () => false });
      return true;
    } catch (err) {
      showError(toUserMessage(err, ERROR_MESSAGES.save));
      return false;
    } finally {
      setUpdatingCommission(false);
    }
  };

  const filteredPartners = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return partners.filter((row) => {
      const matchesSearch =
        !q ||
        row.email.toLowerCase().includes(q) ||
        row.affiliateCode.toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && row.isActive) ||
        (statusFilter === 'inactive' && !row.isActive);
      const createdDay = toLocalDay(row.createdDateTime);
      const matchesFrom = !fromDate || (createdDay && createdDay >= fromDate);
      const matchesTo = !toDate || (createdDay && createdDay <= toDate);
      return matchesSearch && matchesStatus && matchesFrom && matchesTo;
    });
  }, [partners, searchTerm, statusFilter, fromDate, toDate]);

  const handleAddAffiliate = () => {
    if (!newAffiliateEmail.trim()) return;
    alert('Partner invitation is not available yet. Please contact support.');
    setShowAddModal(false);
    setNewAffiliateEmail('');
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewAffiliateEmail('');
  };

  return (
    <div className="page sp-page">
      <div className="page-inner">
        <div className="sp-header-row">
          <ModulePageHeader
            icon={Users}
            title="Sub Partner Listing"
            subtitle="Manage your sub partners and their performance"
            iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
          />
          <button type="button" className="sp-btn sp-btn--primary" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            Add Sub Partner
          </button>
        </div>

        <ModuleAlert message={listError} />

        <section className="module-card sp-toolbar">
          <div className="sp-toolbar__row">
            <div className="sp-search">
              <Search className="w-4 h-4" />
              <input
                type="text"
                placeholder="Search by email or partner code…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="sp-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <label className="sp-date-field">
              <span>From</span>
              <input
                type="date"
                className="sp-select"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </label>
            <label className="sp-date-field">
              <span>To</span>
              <input
                type="date"
                className="sp-select"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>
            {(fromDate || toDate) && (
              <button
                type="button"
                className="sp-btn sp-btn--ghost"
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                }}
              >
                Clear Dates
              </button>
            )}
          </div>
        </section>

        <section className="module-card sp-table-card">
          {listLoading ? (
            <div className="sp-loader">
              <AppLoader color={THEME} />
            </div>
          ) : (
            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Partner Code</th>
                    <th>Commission</th>
                    <th>Total Clients</th>
                    <th>Total Lots</th>
                    <th>Deposit</th>
                    <th>Withdrawal</th>
                    <th>Net</th>
                    <th>Created Date Time</th>
                    <th>Is Active</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPartners.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="sp-empty">
                        No partners found
                      </td>
                    </tr>
                  ) : (
                    filteredPartners.map((row) => (
                      <tr key={row.id}>
                        <td className="sp-email" data-label="Email">{row.email}</td>
                        <td data-label="Partner Code">{row.affiliateCode}</td>
                        <td data-label="Commission">{formatPercent(row.percentage)}</td>
                        <td data-label="Total Clients">{Number.isFinite(row.totalClients) ? row.totalClients : 0}</td>
                        <td data-label="Total Lots">{formatLots(row.totalLots)}</td>
                        <td data-label="Deposit">{formatMoney(row.totalDeposit)}</td>
                        <td data-label="Withdrawal">{formatMoney(row.totalWithdrawal)}</td>
                        <td data-label="Net" style={{ fontWeight: 700, color: row.netDeposit < 0 ? '#b91c1c' : undefined }}>
                          {formatMoney(row.netDeposit)}
                        </td>
                        <td className="sp-muted" data-label="Created Date Time">{formatDateTime(row.createdDateTime)}</td>
                        <td data-label="Is Active">
                          <span className={`sp-pill ${row.isActive ? 'is-active' : 'is-inactive'}`}>
                            {row.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td data-label="Action">
                          {row.subPartnerId > 0 ? (
                            <button
                              type="button"
                              className="sp-update-btn"
                              onClick={() => setCommissionTarget(row)}
                            >
                              {row.percentage != null ? 'Update commission' : 'Commission'}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="sp-footer">
          Showing <strong>{filteredPartners.length ? 1 : 0}</strong> to{' '}
          <strong>{filteredPartners.length}</strong> of <strong>{filteredPartners.length}</strong>{' '}
          results
        </p>
      </div>

      {showAddModal ? (
        <div className="sp-modal-backdrop" role="presentation" onClick={handleCloseModal}>
          <div
            className="sp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sp-add-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sp-modal__head">
              <h3 id="sp-add-title">Add New Partner</h3>
              <button type="button" className="sp-icon-btn" onClick={handleCloseModal} aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="sp-modal__body">
              <label htmlFor="sp-email">Email Address</label>
              <input
                id="sp-email"
                type="email"
                value={newAffiliateEmail}
                onChange={(e) => setNewAffiliateEmail(e.target.value)}
                placeholder="Enter partner email address"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddAffiliate();
                }}
              />
              <p>
                An invitation will be sent to this email address. The partner will be able to join
                your program after accepting the invitation.
              </p>
            </div>
            <div className="sp-modal__foot">
              <button type="button" className="sp-btn sp-btn--ghost" onClick={handleCloseModal}>
                Cancel
              </button>
              <button
                type="button"
                className="sp-btn sp-btn--primary"
                disabled={!newAffiliateEmail.trim()}
                onClick={handleAddAffiliate}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <UpdateCommissionModal
        open={Boolean(commissionTarget)}
        referral={commissionTarget}
        saving={updatingCommission}
        onClose={() => !updatingCommission && setCommissionTarget(null)}
        onSave={handleUpdateCommission}
      />
    </div>
  );
};

export default Affiliates;
